import React, { useEffect, useState } from "react";
import { toast } from "react-toastify";
import { Card, Collapse, Flex, Radio, Space, Switch } from "antd";
import { RightOutlined } from "@ant-design/icons";
import { useThemeContext } from "../../../context/ThemeContext";
import { customLogger } from "../../../utils/Logger";
import SettingsSidebarHeader from "../../../components/common/SettingsSidebarHeader";
import ResynchronizingChatHistroy from "../ResynchronizingChatHistroy";
import ENDPOINTS from "../../../services/endpoint";
import apiInstance from "../../../services/apiService";
import GradientButton from "../../../components/common/UI/GradientButton";
import "./index.scss";

interface SMSSettingProps {
  handleBackToPrevious: () => void;
}

interface PhoneNumbers {
  _id: string;
  isVerified: boolean;
  phoneNumber: string;
}

interface ForwardNumber {
  isForwardSms: boolean;
  smsForwardNumber: string;
}

const { Panel } = Collapse;

const SmsSettings: React.FC<SMSSettingProps> = ({ handleBackToPrevious }) => {
  const { theme } = useThemeContext();
  const [currentScreen, setCurrentScreen] = useState("sms-settings");
  const [loading, setLoading] = useState(false);
  const [phoneNumbers, setphoneNumbers] = useState<PhoneNumbers[]>([]);
  const [selectedNumber, setSelectedNumber] = useState<string | null>(null);
  const [smsForwardNumber, setSmsForwardNumber] =
    useState<ForwardNumber | null>(null);

  const getNumbers = async () => {
    const url = ENDPOINTS.SMS_SETTINGS;
    setLoading(true);
    try {
      const response = await apiInstance.get(url);
      const data = response.data.phoneData;
      setphoneNumbers(data);
      setSmsForwardNumber(response.data.data);
    } catch (error: any) {
      console.error(error);
      toast.error(error.message || "There was an error. Please try again");
    } finally {
      setLoading(false);
    }
  };

  const customHeader = () => (
    <div className="custom-header">
      <GradientButton className="option-icon">
        <img
          src={`/assets/icons/${theme}/incoming-call.png`}
          width={20}
          height={20}
        />
      </GradientButton>
      <span>Forward SMS To Phone No</span>
      <span>{smsForwardNumber?.smsForwardNumber}</span>
    </div>
  );

  const handleNumberChange = async (e: any) => {
    const value = e.target.value;
    if (!value) return;

    setSelectedNumber(value);

    const url = ENDPOINTS.SMS_SETTINGS;

    setLoading(true);

    try {
      const response = await apiInstance.post(url, {
        isForwardSms: true,
        phoneId: value,
      });
      if (response.status === 200) {
        toast.success("SMS setting updated successfully!");
        getNumbers();
      }
    } catch (error: any) {
      customLogger(error);
      toast.error(
        error.message || "Could not update setting. Please try again"
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    getNumbers();
  }, []);

  return (
    <>
      {currentScreen === "sms-settings" && (
        <div className="sms-settings-container">
          <SettingsSidebarHeader
            title="SMS Settings"
            handleBackClick={handleBackToPrevious}
          />

          <div className="options-container">
            <Collapse
              expandIconPosition="end"
              className="custom-collapse forward-number-collapse"
              expandIcon={({ isActive }) => (
                <div className="custom-switch">
                  <Switch checked={isActive} />
                </div>
              )}
            >
              <Panel header={customHeader()} key="1">
                <Radio.Group
                  disabled={loading}
                  className="ring-device-radio-group"
                  value={selectedNumber}
                  onChange={handleNumberChange}
                >
                  {phoneNumbers?.map((number) => (
                    <Radio
                      className="custom-radio"
                      key={number._id}
                      value={number._id}
                    >
                      {number.phoneNumber}
                    </Radio>
                  ))}
                </Radio.Group>
              </Panel>
            </Collapse>

            <Card
              key="resync"
              onClick={() => setCurrentScreen("resync-chat")}
              className="resync-card"
              bordered={false}
              styles={{ body: { padding: "6px 12px" } }}
            >
              <Flex justify="space-between" align="center">
                <Space size={10}>
                  <GradientButton className="option-icon">
                    <img
                      src={`/assets/icons/${theme}/resync.png`}
                      width={20}
                      height={20}
                    />
                  </GradientButton>
                  <h6 className="title">Resync All Chats</h6>
                </Space>
                <RightOutlined style={{ fontSize: "14px", color: "black" }} />
              </Flex>
            </Card>
          </div>
        </div>
      )}

      {currentScreen === "resync-chat" && (
        <ResynchronizingChatHistroy
          handleBackToPrevious={() => setCurrentScreen("sms-settings")}
        />
      )}
    </>
  );
};

export default SmsSettings;
